import { useState } from 'react';
import { PriorityMatrixTab } from '../components/itil/PriorityMatrixTab';
import { ReasonConfigTab } from '../components/itil/ReasonConfigTab';
import { SlaPoliciesTab } from '../components/itil/SlaPoliciesTab';
import { ErrorBoundary } from '../components/ErrorBoundary';

type ItilTab = 'priority-matrix' | 'reason-configuration' | 'sla-policies';

const ITIL_TABS: Array<{ id: ItilTab; label: string; description: string }> = [
  {
    id: 'priority-matrix',
    label: 'Priority Matrix',
    description: 'Manage priority templates and matrix mappings.',
  },
  {
    id: 'reason-configuration',
    label: 'Reason Configuration',
    description: 'Manage resolve, on-hold, and cancel reason templates.',
  },
  {
    id: 'sla-policies',
    label: 'SLA Policies',
    description: 'Manage SLA policy templates and response targets.',
  },
];

function TabErrorFallback({ tabLabel }: { tabLabel: string }) {
  return (
    <div className="card rounded border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
      <p className="font-medium">{tabLabel} is temporarily unavailable.</p>
      <p className="mt-1 text-sm">The rest of the ITIL configuration page is still available.</p>
    </div>
  );
}

export function ItilConfigPage() {
  const [activeTab, setActiveTab] = useState<ItilTab>('priority-matrix');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">ITIL Configuration</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Configure priority matrixes, reason templates, and SLA policies.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="ITIL configuration sections"
        className="flex gap-2 overflow-x-auto border-b border-gray-200 pb-2 dark:border-gray-700"
      >
        {ITIL_TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              id={`itil-tab-${tab.id}`}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`itil-panel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                isActive
                  ? 'bg-primary-700 text-white dark:bg-primary-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
              title={tab.description}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <section
        id="itil-panel-priority-matrix"
        role="tabpanel"
        aria-labelledby="itil-tab-priority-matrix"
        hidden={activeTab !== 'priority-matrix'}
      >
        {activeTab === 'priority-matrix' ? (
          <ErrorBoundary fallback={<TabErrorFallback tabLabel="Priority Matrix" />}>
            <PriorityMatrixTab />
          </ErrorBoundary>
        ) : null}
      </section>

      <section
        id="itil-panel-reason-configuration"
        role="tabpanel"
        aria-labelledby="itil-tab-reason-configuration"
        hidden={activeTab !== 'reason-configuration'}
      >
        {activeTab === 'reason-configuration' ? (
          <ErrorBoundary fallback={<TabErrorFallback tabLabel="Reason Configuration" />}>
            <ReasonConfigTab />
          </ErrorBoundary>
        ) : null}
      </section>

      <section
        id="itil-panel-sla-policies"
        role="tabpanel"
        aria-labelledby="itil-tab-sla-policies"
        hidden={activeTab !== 'sla-policies'}
      >
        {activeTab === 'sla-policies' ? (
          <ErrorBoundary fallback={<TabErrorFallback tabLabel="SLA Policies" />}>
            <SlaPoliciesTab />
          </ErrorBoundary>
        ) : null}
      </section>
    </div>
  );
}
